import React, { type ReactNode } from "react";
import { Router } from "@gatsbyjs/reach-router";
import * as Session from "@/features/auth/services/session";
import { useHasPermission } from "@/features/auth/hooks/usePermission";
import {
  CustomerLicenceOptions,
  useAuthStore,
} from "@/features/auth/stores/authStore";
import {
  useCustomerLicence,
  useProfile,
} from "@/hooks/firestore/user/useProfile";
import { useIsVerified } from "@/hooks/useIsVerified";
import { useShellStore } from "@/features/shell/stores/shellStore";
import FullScreenModal from "@/features/shell/components/FullScreenModal/FullScreenModal";
import { HelpCenter } from "@/features/help-center/components/HelpCenter";
import { navigate } from "@/utilities/navigate";
import { Verification } from "./Verification";

import * as styles from "./VerificationGate.module.scss";

const verificationRequiredCountries = new Set(["AU"]);

export const VerificationGate = ({ children }: { children: ReactNode }) => {
  const isLoggedIn = useAuthStore(
    (state) =>
      state.status === "authenticated" || state.status === "impersonating",
  );
  const isSignUpVisible = useShellStore((state) => state.modal === "signUp");
  const isVerified = useIsVerified();
  const { profile } = useProfile();
  const licence = useCustomerLicence();
  const shouldMigrateToIOMLicence = useHasPermission("migratedAccount", false);

  const requiresVerification =
    isLoggedIn &&
    !isVerified &&
    licence === CustomerLicenceOptions.NTRWC &&
    verificationRequiredCountries.has(profile?.country || "");

  if (!requiresVerification || isSignUpVisible || shouldMigrateToIOMLicence) {
    return children;
  }

  return (
    <FullScreenModal
      contentClassName={styles.modal}
      onClose={async () => {
        await Session.signOut();
        await navigate("/");
      }}
      isOpen={true}
      withBackButton={true}
    >
      <Router primary={false} basepath={`/en-au/account/verification/`}>
        <Verification default />
      </Router>
      <HelpCenter />
    </FullScreenModal>
  );
};
